import './style.scss'
import { Button, Input, Modal } from 'antd'
import { Buffer } from 'buffer'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import wavConverter from 'wav-converter'
import * as homeApi from '@/api/home'
import RedPackage from '@/assets/red-package.gif'
import { PlayFill } from '@/assets/svg/play-fill'
import { UserStore } from '@/global-states'
import { eventTracking, getRandomTexts } from '@/libs/util'
import { CreateVideoStore } from '@/views/create-video/create-video-state'
import { LoadingOutlined, PauseOutlined } from '@ant-design/icons'

interface IProps {
  updateAvatar: (avatar: any) => void
  updateVoice: (voice: any) => void
  updateText: (text: string) => void
}

const SystemGuide: FC<IProps> = (props) => {
  const { t: translate } = useTranslation(['system_guide'])
  const { systemGuideStep, loginStatus } = UserStore
  const { updateAvatar, updateVoice, updateText } = props
  const [avatars, setAvatars] = useState<any[]>([])
  const [voices, setVoices] = useState<any[]>([])
  const [selectAvatar, setSelectAvatar] = useState<any>()
  const [selectVoice, setSelectVoice] = useState<any>()
  const [text, setText] = useState('')
  const [randomKey, setRandomKey] = useState()
  const [previewId, setPreviewId] = useState()
  const [playingId, setPlayingId] = useState()
  const previewIdRef = useRef<any>()
  const audioRef = useRef<any>()

  const open = useMemo(() => {
    return systemGuideStep > 1 && systemGuideStep < 5
  }, [systemGuideStep])

  useEffect(() => {
    setRandomText()
    getSystemGuideConfig()
  }, [])

  useEffect(() => {
    if (open) {
      localStorage.setItem('systemGuideRead', '1')
    }
    getClaimedCoupons()
  }, [open])

  useEffect(() => {
    if (loginStatus) {
      getClaimedCoupons()
    }
  }, [loginStatus])

  useEffect(() => {
    previewIdRef.current = previewId
  }, [previewId])

  const getClaimedCoupons = async () => {
    const res = await UserStore.getClaimedCoupons()
    if (res?.id) {
      CreateVideoStore.couponId = res.id
    }
  }

  const getSystemGuideConfig = async () => {
    const [guideInfo, globalTts, globalHumans] = await Promise.all([
      homeApi.getGuideInfo(),
      homeApi.getGlobalTts(),
      homeApi.getGlobalDigitalHumans()
    ])

    const filterAvatars = guideInfo?.digital_humans?.map((d: any) => {
      const f = globalHumans?.list?.find((g: any) => g.id === d.id)
      return {
        ...f,
        demo_url: d.video_url
      }
    })

    const filterVoices = guideInfo?.tts_voice_ids?.map((id: any) => {
      const f = globalTts?.list?.find((g: any) => g.id === id)
      return f
    })

    setSelectAvatar(filterAvatars?.[0])
    setSelectVoice(filterVoices?.[0])
    setAvatars(filterAvatars)
    setVoices(filterVoices)
  }

  const nextStep = async () => {
    if (systemGuideStep === 2) {
      eventTracking('SystemGuideNextStep2', {
        avatarId: selectAvatar.id
      })
      UserStore.systemGuideStep = systemGuideStep + 1
      updateAvatar(selectAvatar)
    }

    if (systemGuideStep === 3) {
      eventTracking('SystemGuideNextStep3')
      updateText(text)
      UserStore.systemGuideStep = systemGuideStep + 2
    }

    if (systemGuideStep === 4) {
      eventTracking('SystemGuideNextStep4')
      UserStore.systemGuideStep = systemGuideStep + 1
    }
  }

  const setRandomText = () => {
    const r: any = getRandomTexts().filter((t) => t.key !== randomKey)
    const index = Math.floor(Math.random() * r.length)
    setRandomKey(r[index].key)
    setText(r[index].text)
  }

  const clearAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause?.()
      audioRef.current.src = ''
    }
  }

  const closeAudio = () => {
    clearAudio()
    setPlayingId(undefined)
    setPreviewId(undefined)
  }

  const previewTts = async (tts: any, text: string) => {
    if (playingId === tts.id) {
      closeAudio()
      return
    } else if (previewId === tts.id) {
      return
    }

    setPreviewId(tts.id)
    setPlayingId(undefined)
    clearAudio()

    try {
      let res
      if (tts.parent_id) {
        res = await homeApi.previewCommunityVoice(tts.parent_id, tts.id, {
          text,
          voice_parameters: tts.voice_parameters
        })
      } else {
        res = await homeApi.previewTts(tts.id, { text, voice_parameters: tts.voice_parameters })
      }
      if (previewIdRef.current !== tts.id) {
        return
      }
      setPlayingId(tts.id)

      clearAudio()

      const audio = new Audio()
      audio.src = `data:audio/wav;base64,${wavConverter
        .encodeWav(new Buffer(res.audio_base64, 'base64'), {
          numChannels: 1,
          sampleRate: 16000,
          byteRate: 32_000
        })
        .toString('base64')}`
      audio.play()
      audioRef.current = audio

      audio.addEventListener('ended', function () {
        closeAudio()
      })

      audio.addEventListener('pause', function () {
        closeAudio()
      })
    } catch {
      closeAudio()
    }
  }

  const skipStep = () => {
    eventTracking('SystemGuideSkipStep', {
      step: systemGuideStep
    })
    UserStore.systemGuideStep = undefined
  }

  return (
    <>
      <Modal
        title={null}
        open={open}
        width={600}
        footer={null}
        className="system-guide-modal"
        maskClosable={false}
        closeIcon={null}
      >
        <div className="system-guide-step">
          <div className="system-guide-header">
            <div className={`step-item ${systemGuideStep > 1 ? 'actived' : ''}`}>
              <div className="num">1</div>
              <div className="text">{translate('system_guide.step1_text')}</div>
              <div className={`line ${systemGuideStep > 2 ? 'line-actived' : ''}`}></div>
            </div>
            <div className={`step-item ${systemGuideStep > 2 ? 'actived' : ''}`}>
              <div className="num">2</div>
              <div className="text">{translate('system_guide.step2_text')}</div>
              <div className={`line ${systemGuideStep > 3 ? 'line-actived' : ''}`}></div>
            </div>
            <div className={`step-item ${systemGuideStep > 3 ? 'actived' : ''}`}>
              <div className="gif">
                <img src={RedPackage} />
              </div>
              <div className="text">{translate('system_guide.step3_text')}</div>
            </div>
          </div>

          {systemGuideStep === 2 && (
            <div className="guide-step step-one">
              <div className="step-title">{translate('system_guide.step1_title')}</div>
              <div className="step-desc">{translate('system_guide.step1_description')}</div>

              <div className="step-avatar">
                <div className="img-list">
                  {avatars?.map((v) => (
                    <div key={v.id} className="img-list__item">
                      <div className="img-box" onClick={() => setSelectAvatar(v)}>
                        <video loop muted autoPlay controls={false} className="video" src={v.demo_url} />

                        {v.id === selectAvatar?.id && (
                          <>
                            <div className="mask"></div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="step-footer">
                <Button type="primary" onClick={nextStep}>
                  {translate('system_guide.next_button')}
                </Button>
              </div>
            </div>
          )}

          {systemGuideStep === 4 && (
            <div className="guide-step step-one">
              <div className="step-title">{translate('system_guide.step2_title')}</div>
              <div className="step-desc">{translate('system_guide.step2_description')}</div>

              <div className="step-voice">
                <div className="detail-list">
                  {voices?.map((t) => (
                    <div
                      className={`detail-list__item ${selectVoice.name === t.name ? 'actived' : ''}`}
                      key={t.id}
                      onClick={() => setSelectVoice(t)}
                    >
                      <div
                        className="left"
                        onClick={(e) => {
                          e.stopPropagation()
                          previewTts(t, t.preview_text || translate('system_guide.default_preview_text'))
                        }}
                      >
                        {previewId === t.id ? (
                          playingId === t.id ? (
                            <PauseOutlined />
                          ) : (
                            <LoadingOutlined />
                          )
                        ) : (
                          <PlayFill className="play" />
                        )}
                      </div>
                      <div className="center">
                        <div className={`name ellipsis ${selectVoice.name === t.name ? 'gradient' : ''}`}>
                          {t.display_name}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="step-footer mt0">
                <Button type="primary" onClick={nextStep}>
                  {translate('system_guide.next_button')}
                </Button>
              </div>
            </div>
          )}

          {systemGuideStep === 3 && (
            <div className="guide-step step-one">
              <div className="step-title">{translate('system_guide.step3_title')}</div>
              <div className="step-desc">{translate('system_guide.step3_description')}</div>

              <div className="step-text">
                <div className="box">
                  <Input.TextArea
                    value={text}
                    placeholder={translate('system_guide.textarea_placeholder')}
                    className="textarea"
                    maxLength={3000}
                    onChange={(e) => setText(e.target.value)}
                  />
                  <div className="btn" onClick={setRandomText}>
                    <span className="gradient">{translate('system_guide.random_text_button')}</span>
                  </div>
                </div>
              </div>

              <div className="step-footer">
                <Button disabled={text?.length < 5} type="primary" onClick={nextStep}>
                  {translate('system_guide.next_button')}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="skip-step" onClick={skipStep}>
          {translate('system_guide.skip_button', { step: systemGuideStep - 1 })}
        </div>
      </Modal>
    </>
  )
}

export default SystemGuide
